import supabase from '../lib/supabase'

// Demo data for offline mode
const demoClients = [
  {
    id: 1,
    name: 'John Smith',
    business_name: 'TechCorp Ltd',
    email: 'john@techcorp.com',
    phone: '+1 (555) 123-4567',
    address: '123 Tech Street, Silicon Valley, CA 94000',
    billing_contact: {
      name: 'Sarah Johnson',
      email: 'billing@techcorp.com',
      phone: '+1 (555) 123-4568'
    },
    gstin: '27AABCT1332L000',
    gst_type: 'B2B',
    gst_rate: 18,
    contract_details: {
      start_date: '2024-01-15',
      end_date: '2024-12-15',
      renewal_terms: 'Auto-renewal with 30 days notice'
    },
    service_history: [
      { service: 'SEO', start_date: '2024-01-15', status: 'Active' },
      { service: 'PPC', start_date: '2024-02-01', status: 'Active' }
    ],
    payment_terms: {
      due_days: 30,
      early_payment_discount: 2,
      late_payment_penalty: 1.5
    },
    segmentation: {
      service_type: 'SEO',
      billing_frequency: 'Monthly',
      account_manager: 'Alice Cooper',
      payment_status: 'Active'
    },
    status: 'Active',
    projects: 3,
    total_billed: 15500,
    monthly_revenue: 2500,
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-01-15T00:00:00Z'
  },
  {
    id: 2,
    name: 'Michael Brown',
    business_name: 'StartupXYZ',
    email: 'mike@startupxyz.com',
    phone: '+1 (555) 987-6543',
    address: '456 Innovation Ave, Austin, TX 78701',
    billing_contact: {
      name: 'Emily Davis',
      email: 'finance@startupxyz.com',
      phone: '+1 (555) 987-6544'
    },
    gstin: '07AABCS5555N000',
    gst_type: 'B2B',
    gst_rate: 18,
    contract_details: {
      start_date: '2024-02-01',
      end_date: '2025-01-31',
      renewal_terms: 'Manual renewal required'
    },
    service_history: [
      { service: 'PPC', start_date: '2024-02-01', status: 'Active' },
      { service: 'Social Media Marketing', start_date: '2024-03-01', status: 'Active' }
    ],
    payment_terms: {
      due_days: 45,
      early_payment_discount: 3,
      late_payment_penalty: 2
    },
    segmentation: {
      service_type: 'PPC',
      billing_frequency: 'Quarterly',
      account_manager: 'Bob Wilson',
      payment_status: 'Pending'
    },
    status: 'Active',
    projects: 2,
    total_billed: 8200,
    monthly_revenue: 1800,
    created_at: '2024-02-01T00:00:00Z',
    updated_at: '2024-02-01T00:00:00Z'
  },
  {
    id: 3,
    name: 'Lisa Anderson',
    business_name: 'E-commerce Plus',
    email: 'lisa@ecommerceplus.com',
    phone: '+1 (555) 456-7890',
    address: '789 Commerce Blvd, New York, NY 10001',
    billing_contact: {
      name: 'David Miller',
      email: 'accounts@ecommerceplus.com',
      phone: '+1 (555) 456-7891'
    },
    gstin: '',
    gst_type: 'B2C',
    gst_rate: 0,
    contract_details: {
      start_date: '2023-12-01',
      end_date: '2024-11-30',
      renewal_terms: 'Auto-renewal with 60 days notice'
    },
    service_history: [
      { service: 'Web Development', start_date: '2023-12-01', status: 'Completed' },
      { service: 'SEO', start_date: '2024-01-01', status: 'Paused' }
    ],
    payment_terms: {
      due_days: 30,
      early_payment_discount: 0,
      late_payment_penalty: 1
    },
    segmentation: {
      service_type: 'Web Development',
      billing_frequency: 'Annual',
      account_manager: 'Carol White',
      payment_status: 'Overdue'
    },
    status: 'Inactive',
    projects: 1,
    total_billed: 3200,
    monthly_revenue: 0,
    created_at: '2023-12-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  }
];

class ClientService {
  constructor() {
    this.isOnline = false;
    this.checkConnection();
  }

  async checkConnection() {
    try {
      const { data, error } = await supabase.from('clients_billing_2024').select('count', { count: 'exact' });
      this.isOnline = !error;
    } catch (error) {
      this.isOnline = false;
    }
  }

  async getAllClients() {
    if (!this.isOnline) {
      return { data: demoClients, error: null };
    }

    try {
      const { data, error } = await supabase
        .from('clients_billing_2024')
        .select('*')
        .order('created_at', { ascending: false });

      return { data: data || [], error };
    } catch (error) {
      return { data: demoClients, error: null };
    }
  }

  async getClient(id) {
    if (!this.isOnline) {
      const client = demoClients.find(c => c.id === parseInt(id));
      return { data: client, error: client ? null : 'Client not found' };
    }

    try {
      const { data, error } = await supabase
        .from('clients_billing_2024')
        .select('*')
        .eq('id', id)
        .single();

      return { data, error };
    } catch (error) {
      const client = demoClients.find(c => c.id === parseInt(id));
      return { data: client, error: client ? null : 'Client not found' };
    }
  }

  async createClient(clientData) {
    if (!this.isOnline) {
      const newClient = {
        ...clientData,
        id: Math.max(...demoClients.map(c => c.id)) + 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      demoClients.push(newClient);
      return { data: newClient, error: null };
    }

    try {
      const { data, error } = await supabase
        .from('clients_billing_2024')
        .insert([clientData])
        .select()
        .single();

      return { data, error };
    } catch (error) {
      // Fallback to demo mode
      const newClient = {
        ...clientData,
        id: Math.max(...demoClients.map(c => c.id)) + 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      demoClients.push(newClient);
      return { data: newClient, error: null };
    }
  }

  async updateClient(id, clientData) {
    if (!this.isOnline) {
      const index = demoClients.findIndex(c => c.id === parseInt(id));
      if (index !== -1) {
        demoClients[index] = {
          ...demoClients[index],
          ...clientData,
          updated_at: new Date().toISOString()
        };
        return { data: demoClients[index], error: null };
      }
      return { data: null, error: 'Client not found' };
    }

    try {
      const { data, error } = await supabase
        .from('clients_billing_2024')
        .update({ ...clientData, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      // Fallback to demo mode
      const index = demoClients.findIndex(c => c.id === parseInt(id));
      if (index !== -1) {
        demoClients[index] = {
          ...demoClients[index],
          ...clientData,
          updated_at: new Date().toISOString()
        };
        return { data: demoClients[index], error: null };
      }
      return { data: null, error: 'Client not found' };
    }
  }

  async deleteClient(id) {
    if (!this.isOnline) {
      const index = demoClients.findIndex(c => c.id === parseInt(id));
      if (index !== -1) {
        const deleted = demoClients.splice(index, 1)[0];
        return { data: deleted, error: null };
      }
      return { data: null, error: 'Client not found' };
    }

    try {
      const { data, error } = await supabase
        .from('clients_billing_2024')
        .delete()
        .eq('id', id)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      // Fallback to demo mode
      const index = demoClients.findIndex(c => c.id === parseInt(id));
      if (index !== -1) {
        const deleted = demoClients.splice(index, 1)[0];
        return { data: deleted, error: null };
      }
      return { data: null, error: 'Client not found' };
    }
  }

  async getClientsBySegment(segment, value) {
    const { data: clients } = await this.getAllClients();
    
    return {
      data: clients.filter(client => {
        switch (segment) {
          case 'service_type':
            return client.segmentation?.service_type === value;
          case 'billing_frequency':
            return client.segmentation?.billing_frequency === value;
          case 'gst_type':
            return client.gst_type === value;
          case 'payment_status':
            return client.segmentation?.payment_status === value;
          case 'account_manager':
            return client.segmentation?.account_manager === value;
          default:
            return false;
        }
      }),
      error: null
    };
  }
}

export default new ClientService();